import { useState, useCallback, useMemo } from "react";
import { uid, now, deepClone, findNode } from "../utils";

function validateTemplateRefs(editingTemplate, servicesRef, resourcesRef) {
  const validServiceIds = new Set((servicesRef || []).map((s) => s.id));
  const validResourceIds = new Set((resourcesRef || []).map((r) => r.id));
  const invalidServiceIds = editingTemplate.services
    .map((s) => s.serviceId)
    .filter((id) => id != null && !validServiceIds.has(id));
  const invalidResourceIds = editingTemplate.resources
    .map((r) => r.resourceId)
    .filter((id) => id != null && !validResourceIds.has(id));
  const missingService = editingTemplate.services.some((s) => s.serviceId == null);
  const missingResource = editingTemplate.resources.some((r) => r.resourceId == null);
  if (
    (invalidServiceIds.length || invalidResourceIds.length || missingService || missingResource) &&
    (servicesRef.length || resourcesRef.length)
  ) {
    const parts = [];
    if (invalidServiceIds.length || missingService) parts.push("services");
    if (invalidResourceIds.length || missingResource) parts.push("resources");
    return parts;
  }
  return null;
}

function buildTemplatePayload(editingTemplate) {
  const services = editingTemplate.services
    .filter((s) => s.serviceId != null)
    .map((s) => ({
      serviceId: s.serviceId,
      available: s.available,
      availType: s.availType,
      autoProv: s.autoProv,
      sso: s.sso,
      assignable: s.assignable,
      automatedProvisioningAssn: s.automatedProvisioningAssn,
      externalId: s.externalId,
      locationSpcfcUrl: s.locationSpcfcUrl,
      externalGroup: s.externalGroup,
      explorable: s.explorable,
    }));
  const resources = editingTemplate.resources
    .filter((r) => r.resourceId != null)
    .map((r) => ({
      resourceId: r.resourceId,
      active: r.active,
      autoAssign: r.autoAssign,
      hidden: r.hidden,
    }));
  return {
    name: editingTemplate.name.trim(),
    description: editingTemplate.desc?.trim() || null,
    services,
    resources,
  };
}

const emptyTemplate = () => ({
  id: null,
  name: "",
  desc: "",
  services: [],
  resources: [],
  updated: now(),
});

const defaultService = () => ({
  _key: uid(),
  serviceId: null,
  available: true,
  availType: "ALL_LEARNERS",
  autoProv: false,
  sso: false,
  assignable: false,
  automatedProvisioningAssn: false,
  externalId: undefined,
  locationSpcfcUrl: undefined,
  externalGroup: undefined,
  explorable: false,
});

const defaultResource = () => ({
  _key: uid(),
  resourceId: null,
  active: true,
  autoAssign: false,
  hidden: false,
});

/**
 * @param {Array} initialTemplates - Initial template list; when apiOptions is provided, this is the source of truth (parent refetches and passes updated list).
 * @param {Function} getConfig - (locationId) => { services, resources }
 * @param {Function} setTab - (tabId) => void
 * @param {{ api?: object, refetchTemplates?: () => Promise<void>, locations?: Array }} [apiOptions] - When provided, save/delete call API then refetchTemplates; locations used for openTemplateFromLocation label; templates = initialTemplates.
 */
export function useTemplateState(initialTemplates, getConfig, setTab, apiOptions) {
  const [localTemplates, setLocalTemplates] = useState(initialTemplates);
  const templates = apiOptions ? initialTemplates : localTemplates;
  const setTemplates = useMemo(() => (apiOptions ? () => {} : setLocalTemplates), [apiOptions]);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateExpandedServices, setTemplateExpandedServices] = useState(new Set());
  const [templateExpandedResources, setTemplateExpandedResources] = useState(new Set());
  const [templateSaving, setTemplateSaving] = useState(false);
  const [templateDeleting, setTemplateDeleting] = useState(false);
  const locations = useMemo(() => apiOptions?.locations ?? [], [apiOptions?.locations]);

  const toggleExpanded = useCallback((id, setter) => {
    setter((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const openNewTemplate = useCallback(() => {
    setEditingTemplate(emptyTemplate());
    setTemplateExpandedServices(new Set());
    setTemplateExpandedResources(new Set());
    setTab("templates");
  }, [setTab]);

  const openEditTemplate = useCallback((template) => {
    const clone = deepClone(template);
    clone.services = clone.services.map((s) => ({ _key: uid(), ...s }));
    clone.resources = clone.resources.map((r) => ({ _key: uid(), ...r }));
    setEditingTemplate(clone);
    setTemplateExpandedServices(new Set());
    setTemplateExpandedResources(new Set());
  }, []);

  const openTemplateFromLocation = useCallback(
    (locationId) => {
      const locationConfig = getConfig(locationId);
      if (!locationConfig) return;
      const locationName = findNode(locations, locationId)?.name || locationId;
      const servicesRef = apiOptions?.servicesRef ?? [];
      const resourcesRef = apiOptions?.resourcesRef ?? [];
      // Only include config applied at the selected location (direct). Exclude inherited from parents; child config is not in the response.
      const directServices = (locationConfig.services ?? []).filter((s) => s.origin === "direct");
      const directResources = (locationConfig.resources ?? []).filter((r) => r.origin === "direct");
      const services = directServices
        .map((service) => {
          const ref = servicesRef.find((r) => r.name === service.name);
          if (!ref) return null;
          return {
            _key: uid(),
            serviceId: ref.id,
            available: service.available,
            availType: service.availType,
            autoProv: service.autoProv,
            sso: service.sso,
            assignable: service.assignable,
            automatedProvisioningAssn: service.automatedProvisioningAssn,
            externalId: service.externalId,
            locationSpcfcUrl: service.locationSpcfcUrl,
            externalGroup: service.externalGroup,
            explorable: service.explorable,
          };
        })
        .filter(Boolean);
      const resources = directResources
        .map((resource) => {
          const ref = resourcesRef.find((x) => x.name === resource.name);
          if (!ref) return null;
          return {
            _key: uid(),
            resourceId: ref.id,
            active: resource.active,
            autoAssign: resource.autoAssign,
            hidden: resource.hidden,
          };
        })
        .filter(Boolean);
      setEditingTemplate({
        id: null,
        name: `${locationName} Config`,
        desc: `Generated from ${locationName}`,
        services,
        resources,
        updated: now(),
      });
      setTemplateExpandedServices(new Set());
      setTemplateExpandedResources(new Set());
      setTab("templates");
    },
    [getConfig, setTab, locations, apiOptions]
  );

  const saveTemplate = useCallback(
    async (notify) => {
      if (!editingTemplate.name.trim()) {
        notify("Name required", "error");
        return;
      }
      const api = apiOptions?.api;
      const refetchTemplates = apiOptions?.refetchTemplates;
      if (api && refetchTemplates) {
        const serviceRefs = apiOptions?.servicesRef ?? [];
        const resourceRefs = apiOptions?.resourcesRef ?? [];
        const invalidParts = validateTemplateRefs(editingTemplate, serviceRefs, resourceRefs);
        if (invalidParts) {
          notify(`The following are not in the catalog: ${invalidParts.join(", ")}. Remove or fix them.`, "error");
          return;
        }
        setTemplateSaving(true);
        try {
          const payload = buildTemplatePayload(editingTemplate);
          if (editingTemplate.id) {
            await api.updateTemplate(String(editingTemplate.id), payload);
            notify(`"${editingTemplate.name}" updated`);
          } else {
            await api.createTemplate(payload);
            notify(`"${editingTemplate.name}" created`);
          }
          await refetchTemplates();
          setEditingTemplate(null);
        } catch (e) {
          notify(e?.message || "Save failed", "error");
        } finally {
          setTemplateSaving(false);
        }
        return;
      }
      if (editingTemplate.id) {
        setTemplates((prev) =>
          prev.map((t) => (t.id === editingTemplate.id ? { ...editingTemplate, updated: now() } : t))
        );
        notify(`"${editingTemplate.name}" updated`);
      } else {
        setTemplates((prev) => [...prev, { ...editingTemplate, id: uid(), updated: now() }]);
        notify(`"${editingTemplate.name}" created`);
      }
      setEditingTemplate(null);
    },
    [editingTemplate, apiOptions, setTemplates]
  );

  const deleteTemplate = useCallback(
    async (id, notify) => {
      const api = apiOptions?.api;
      const refetchTemplates = apiOptions?.refetchTemplates;
      const template = templates.find((x) => x.id === id);
      if (api && refetchTemplates) {
        setTemplateDeleting(true);
        try {
          await api.deleteTemplate(String(id));
          await refetchTemplates();
          notify(`"${template?.name}" deleted`);
        } catch (e) {
          notify(e?.message || "Delete failed", "error");
        } finally {
          setTemplateDeleting(false);
        }
        return;
      }
      setTemplates((prev) => prev.filter((x) => x.id !== id));
      notify(`"${template?.name}" deleted`);
    },
    [templates, apiOptions, setTemplates]
  );

  const templateUpdateService = useCallback((index, data) => {
    setEditingTemplate((prev) => ({
      ...prev,
      services: prev.services.map((service, j) => (j === index ? data : service)),
    }));
  }, []);

  const templateRemoveService = useCallback((index) => {
    setEditingTemplate((prev) => ({ ...prev, services: prev.services.filter((_, j) => j !== index) }));
    setTemplateExpandedServices((prev) => {
      const next = new Set();
      [...prev].forEach((x) => {
        if (x < index) next.add(x);
        else if (x > index) next.add(x - 1);
      });
      return next;
    });
  }, []);

  const templateAddService = useCallback(() => {
    setTemplateExpandedServices((expanded) => new Set([0, ...[...expanded].map((i) => i + 1)]));
    setEditingTemplate((prev) => ({ ...prev, services: [defaultService(), ...prev.services] }));
  }, []);

  const templateUpdateResource = useCallback((index, data) => {
    setEditingTemplate((prev) => ({
      ...prev,
      resources: prev.resources.map((resource, j) => (j === index ? data : resource)),
    }));
  }, []);

  const templateRemoveResource = useCallback((index) => {
    setEditingTemplate((prev) => ({ ...prev, resources: prev.resources.filter((_, j) => j !== index) }));
    setTemplateExpandedResources((prev) => {
      const next = new Set();
      [...prev].forEach((x) => {
        if (x < index) next.add(x);
        else if (x > index) next.add(x - 1);
      });
      return next;
    });
  }, []);

  const templateAddResource = useCallback(() => {
    setTemplateExpandedResources((expanded) => new Set([0, ...[...expanded].map((i) => i + 1)]));
    setEditingTemplate((prev) => ({ ...prev, resources: [defaultResource(), ...prev.resources] }));
  }, []);

  return {
    templates,
    setTemplates,
    editingTemplate,
    setEditingTemplate,
    templateExpandedServices,
    setTemplateExpandedServices,
    templateExpandedResources,
    setTemplateExpandedResources,
    toggleExpanded,
    openNewTemplate,
    openEditTemplate,
    openTemplateFromLocation,
    saveTemplate,
    deleteTemplate,
    templateUpdateService,
    templateRemoveService,
    templateAddService,
    templateUpdateResource,
    templateRemoveResource,
    templateAddResource,
    templateSaving,
    templateDeleting,
  };
}
